import "./Onboarding.css";
import React, { useEffect, useState } from "react";
import {
  BackButton,
  Content,
  Header,
  Main,
} from "../commons/Template/Template";
import { Form, Input } from "../commons/Form/Form";
import Button from "../commons/Button/Button";
import {
  AccountBalanceWallet,
  ArrowCircleLeft,
  ArrowCircleRight,
  ArrowOutward,
  AutoAwesome,
  BusinessCenter,
  CallMade,
  Done,
  LibraryBooks,
  LocalShipping,
  Mail,
  MonitorHeart,
  Password,
  RecentActors,
  Send,
  Timeline,
} from "@mui/icons-material";
import ProgressBar from "../Financing/Common/ProgressBar/ProgressBar";
import { generateImgMap, useOnboarding } from "./OnboardingContext";
import { Trans, useTranslation } from "react-i18next";
import { formatSIREN } from "../../utils";
import Card, {
  Cards,
  CardText,
  CardTextHeading,
  CardTextSubheading,
} from "../commons/Card/Card";
import Address from "../commons/Address/Address";
import api from "../../api/resources";
import PasswordResetKickOff from "../Account/PasswordResetKickOff";
import { LegalConditionsLabel } from "../Account/SignUp";
import { PartnersGrid } from "../Treasury/Components/Partners/Partners";
import usePartners from "../Financing/usePartners";
import ImageContainer from "../commons/ImageContainer/ImageContainer";
import { PieChart } from "react-feather";
import Typo from "../commons/Typo/Typo";
import { useNavigate, useSearchParams } from "react-router-dom";
import routes from "../../routes/routes";

const supportImagesContext = import.meta.glob("../../assets/support/*.png", {
  eager: true,
});

export function OnboardingRedirect({ children }) {
  let [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const redirectSearchParams = async () => {
    const urlExpectedKeys = [
      "first_name",
      "last_name",
      "email",
      "telephone",
      "ext_id",
    ];

    const urlToDataKeyMap = {
      last_name: "surname",
    };

    const urlData = {};
    for (const key of urlExpectedKeys) {
      const value = searchParams.get(key);
      urlData[urlToDataKeyMap[key] || key] = value ?? "";
    }

    navigate(routes.Onboarding.root, {
      state: { urlData },
      replace: true,
    });
  };

  useEffect(() => {
    if (searchParams.size) {
      redirectSearchParams();
    }
  }, []);

  if (searchParams.size) return <></>;
  return children;
}

export default function Onboarding() {
  const {
    steps,
    resultsShown,
    signUpShown,
    data,
    results,
    userID,
    handleSignUp,
  } = useOnboarding();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [signUpShown, resultsShown]);

  if (signUpShown) return <PasswordResetKickOff onboardingEmail={data.email} />;

  if (steps.every((step) => step.done) && results && userID && resultsShown)
    return <Results onSignUp={handleSignUp} />;

  return <Steps />;
}

function Steps() {
  const { steps, selectedStep, currentStepIndex, handleBack } = useOnboarding();

  const { t } = useTranslation(["common", "onboarding"]);

  document.title = t("onboarding:title");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [selectedStep]);

  const fixerHeight = "4rem";

  return (
    <Main themeSwitch>
      <Content>
        <Fixer height={fixerHeight} style={{ borderBottom: "var(--border)" }}>
          <ProgressContainer>
            <div
              style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}
            >
              {currentStepIndex ? (
                <BackButton
                  className={"ProgressBarBackButton"}
                  onClick={currentStepIndex ? handleBack : null}
                />
              ) : null}
              <Typo variant={"h1"} style={{ fontSize: "larger" }}>
                {t("onboarding:title")}
              </Typo>
            </div>

            <div style={{ flexGrow: 1 }}>
              <ProgressBar
                colorLastStep={false}
                length={steps.length}
                index={currentStepIndex}
              />
            </div>
          </ProgressContainer>
        </Fixer>

        <div style={{ marginTop: fixerHeight, marginBottom: fixerHeight }}>
          <Step />
        </div>

        <Fixer
          positioning={{ bottom: "0rem" }}
          style={{ borderTop: "var(--border)" }}
        >
          <Supporters />
        </Fixer>
      </Content>
    </Main>
  );
}

function ProgressContainer({ children }) {
  return (
    <div
      style={{
        display: "flex",
        gap: "2rem",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
      }}
    >
      {children}
    </div>
  );
}

function Fixer({
  children,
  height,
  positioning = {
    top: "4rem", // height of app menu
  },
  style,
}) {
  return (
    <div
      style={{
        backgroundColor: "white",
        zIndex: "3",
        position: "fixed",
        ...positioning,
        left: 0,
        right: 0,
        boxSizing: "border-box",
        ...style,
      }}
    >
      <div
        style={{
          maxWidth: "var(--mainMaxWidth)",
          minWidth: "var(--appMinWidth)",
          boxSizing: "border-box",
          margin: "0 auto",
          padding: "0rem var(--appPadding)",
          height,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function Supporters() {
  const { t } = useTranslation(["common", "onboarding"]);
  const supportImgMap = generateImgMap(supportImagesContext);

  const [supporters] = useState(
    Object.keys(supportImgMap).map((supporterKey) => ({
      id: supporterKey,
      imgSrc: supportImgMap[supporterKey],
      alt: t("onboarding:supporters.list." + supporterKey),
    })),
  );

  const caption = t("onboarding:supporters.caption");

  return (
    <div className="SupporterLogosContainer">
      <Typo
        variant={"body4"}
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={caption}
      >
        {caption}
      </Typo>
      {supporters.map((supporter) => (
        <ImageContainer
          key={supporter.id}
          className="SupporterLogoImageContainer"
          src={supporter.imgSrc}
          alt={supporter.alt}
        />
      ))}
    </div>
  );
}

function Step() {
  const { steps, selectedStep } = useOnboarding();

  const { t } = useTranslation(["common", "onboarding"]);

  const StepComponent = steps.find(
    (step) => step.id === selectedStep,
  ).component;

  const AsideComponent = steps.find(
    (step) => step.id === selectedStep,
  ).asideComponent;

  return (
    <div
      style={{
        display: " flex",
        gap: "2rem",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        marginBottom: "2rem",
      }}
    >
      <Placeholder>
        <StepContainer>
          <div>
            <Typo variant="h2" style={{ marginBottom: "0.5rem" }}>
              {t(`onboarding:forms.${selectedStep}.title`)}
            </Typo>
            <Typo variant="body2">
              {t(`onboarding:forms.${selectedStep}.subtitle`)}
            </Typo>
          </div>

          <StepComponent />
        </StepContainer>
      </Placeholder>

      <Placeholder>
        <AsideComponent />
      </Placeholder>
    </div>
  );
}

function StepContainer({ children }) {
  return <div className="StepContainer">{children}</div>;
}

function Placeholder({ children, style }) {
  const placeholderStyle = {
    flexGrow: "1",
    flexBasis: "10rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    ...style,
  };

  return <div style={placeholderStyle}>{children}</div>;
}

function Results({ onSignUp }) {
  const { results } = useOnboarding();
  const { t } = useTranslation(["common", "onboarding"]);

  const title = t("onboarding:results.title");
  document.title = title;

  const handleNext = () => {
    onSignUp();
  };

  return (
    <Main themeSwitch>
      <Header>
        <Typo variant={"h1"}>{title}</Typo>
        <Typo variant={"body2"}>{t("onboarding:results.subtitle")}</Typo>
      </Header>
      <Content
        style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
      >
        <ResultsList results={results} />
        <ResultsAfter onAfter={handleNext} />
        <Supporters />
      </Content>
    </Main>
  );
}

function ResultsList({ results }) {
  const { t } = useTranslation(["common", "onboarding", "financing"]);

  const iconsMap = {
    clients: <RecentActors />,
    long_term: <Timeline />,
    short_term: <AccountBalanceWallet />,
    stock: <LocalShipping />,
    vendors: <LibraryBooks />,
  };

  const manyResults = Object.entries(results).length > 2;

  return (
    <div
      className={[manyResults ? "ResultsListGrid" : "ResultsListFlex", "Paper"]
        .filter(Boolean)
        .join(" ")}
    >
      {Object.entries(results).map(([id, eligible]) => (
        <div
          className={["ResultCard", !manyResults && "ResultCardFlexItem"]
            .filter(Boolean)
            .join(" ")}
          key={id}
          style={{
            border: eligible ? "1px solid var(--color-success-3)" : "unset",
          }}
        >
          <IconContainer
            style={{
              backgroundColor: `var(--color-need-${id})`,
              height: "100%",
              boxSizing: "border-box",
              padding: "1.5rem",
            }}
          >
            {iconsMap[id]}
          </IconContainer>

          <div
            style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}
          >
            <Typo variant={"body5"}>
              {t(`financing:assessment.results.keys.${id}.name`)}
            </Typo>
            <Typo
              variant={"body4"}
              className={"ResultDescription"}
              title={t(`financing:assessment.results.keys.${id}.description`)}
            >
              {t(`financing:assessment.results.keys.${id}.description`)}
            </Typo>
          </div>
        </div>
      ))}
    </div>
  );
}

function ResultsAfter({ onAfter }) {
  const { t } = useTranslation(["common", "onboarding"]);

  const iconsMap = {
    offers: <ArrowOutward />,
    eligibility: <CallMade />,
    products: <BusinessCenter />,
  };

  const keys = Object.keys(iconsMap);

  const tKey = "onboarding:results.after.list.";

  return (
    <div
      style={{
        borderRadius: "var(--borderRadiusMedium)",
        display: "flex",
        gap: "1rem",
        flexWrap: "wrap",
      }}
    >
      <div
        style={{
          flexBasis: "45rem",
          flexGrow: "1",
          padding: "1.5rem",
          border: "var(--border)",
          borderRadius: "var(--borderRadiusMedium)",
        }}
      >
        <Typo
          variant={"body5"}
          style={{ marginBottom: "1rem", maxWidth: "100%" }}
        >
          {t("onboarding:results.after.heading")}
        </Typo>
        <ul
          style={{
            marginBottom: "0",
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
          }}
        >
          {keys.map((key) => (
            <li style={{ display: "flex", gap: "0.5rem" }} key={key}>
              <Done style={{ color: "var(--color-success-1)" }} />{" "}
              {t(tKey + key)}
            </li>
          ))}
        </ul>
      </div>

      <div
        className="Paper"
        style={{
          padding: "1.5rem",
          paddingTop: "2rem",
          flexBasis: "10rem",
          flexGrow: "1",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Button
          variant="primary_a"
          text={t("onboarding:results.after.button")}
          onClick={onAfter}
          style={{}}
        />

        <Typo variant="body3" style={{ textAlign: "center" }}>
          {t("onboarding:results.after.footer")}
        </Typo>
      </div>
    </div>
  );
}

function useSelectInputs(inputKeys = [""]) {
  const { t } = useTranslation(["common", "onboarding"]);
  const { data, handleDataChange } = useOnboarding();

  const initialSelectValues = Object.fromEntries(
    inputKeys.map((key) => [key, data[key]]),
  );
  // {inputKey1: {label: "", value: ""}, inputKey2: {label: "", value: ""}}
  const [selectValues, setSelectValues] = useState(initialSelectValues);

  const handleChange = (key, value) => {
    setSelectValues((prev) => ({ ...prev, [key]: value }));
    handleDataChange(key, value);
  };

  const genOptions = (key) =>
    Object.entries(
      t(`onboarding:inputs.${key}.options`, { returnObjects: true }),
    ).map(([i18nKey, stringValue]) => ({ label: stringValue, value: i18nKey }));

  const inputs = inputKeys.map((inputKey) => ({
    type: "select",
    isClearable: true,
    autoSortOptionsDisabled: true,
    floatingLabel: true,
    label: getDataLabel(t, inputKey),
    value: selectValues[inputKey],
    onChange: (value) => handleChange(inputKey, value),
    options: genOptions(inputKey),
  }));

  return { selectValues, inputs };
}

function getDataLabel(t, dataKey) {
  return t(`onboarding:inputs.${dataKey}.label`);
}

function getDataTextValue(t, dataKey, enumValue) {
  return t(`onboarding:inputs.${dataKey}.options.${enumValue}`);
}

export function Needs() {
  const { handleConfirm } = useOnboarding();

  const { inputs } = useSelectInputs(["amount", "need"]);

  return (
    <Form className="OnboardingForm" onSubmit={handleConfirm}>
      {inputs.map((input, index) => (
        <Input key={index} {...input} />
      ))}
    </Form>
  );
}

export const FinancialDataKeys = [
  "turnover",
  "equity",
  "gross_operating_profit",
  "net_income",
];

export function FinancialData() {
  const { handleConfirm, assessment, handleSubmitDataForAssessment } =
    useOnboarding();

  const { inputs } = useSelectInputs(FinancialDataKeys);

  return (
    <Form
      className="OnboardingForm"
      onSubmit={assessment ? handleSubmitDataForAssessment : handleConfirm}
    >
      {inputs.map((input, index) => (
        <Input key={index} {...input} />
      ))}
    </Form>
  );
}

function ConfirmationButton({ onClick }) {
  const { t } = useTranslation(["common", "onboarding"]);

  const confirmButton = {
    style: { marginTop: "auto", height: "3rem" },
    variant: "primary_a",
    text: t("common:confirm"),
  };

  return <Button {...confirmButton} onClick={onClick} />;
}

function ModificationButton({ onClick }) {
  const { t } = useTranslation(["common", "onboarding"]);

  const modButton = {
    style: { width: "fit-content", alignSelf: "center" },
    variant: "link",
    text: t("common:modify"),
  };

  return <Button {...modButton} onClick={onClick} />;
}

export function CompanyData() {
  const { handleConfirm, handleDataChange, data } = useOnboarding();

  const [localCompany, setLocalCompany] = useState(data.company);

  const [searchResults, setSearchResults] = useState([]);

  const handleConfirmCode = (searchResult) => {
    handleDataChange("company", searchResult);
    handleConfirm();
  };

  const handleEditCompany = () => {
    setLocalCompany(null);
  };

  const resetSearch = () => {
    setSearchResults([]);
  };

  if (localCompany) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <CompanyDisplayCards>
          <CompanyDisplayCard localCompany={localCompany} />
        </CompanyDisplayCards>

        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <ModificationButton onClick={handleEditCompany} />
          <ConfirmationButton onClick={handleConfirm} />
        </div>
      </div>
    );
  }

  if (searchResults.length)
    return (
      <CompanyConfirmSearch
        searchResults={searchResults}
        handleConfirmCode={handleConfirmCode}
        handleReset={resetSearch}
      />
    );

  return <CompanySearch onSearchResults={setSearchResults} />;
}

export function CompanyDisplayCard({ localCompany, onClick }) {
  return (
    <Card className={"CompanyDisplayCard"} onClick={onClick}>
      <CardText>
        {localCompany && (
          <CardTextHeading title={localCompany.name}>
            {localCompany.name}
          </CardTextHeading>
        )}
        <CardTextSubheading title={localCompany.code}>
          SIREN {formatSIREN(localCompany.code)}
        </CardTextSubheading>

        {localCompany.address && (
          <CardTextSubheading
            title={Object.values(localCompany.address)
              .filter(Boolean)
              .join(" ")}
          >
            <Address address={localCompany.address} />
          </CardTextSubheading>
        )}
      </CardText>
    </Card>
  );
}

function CompanySearch({ onSearchResults }) {
  const { t } = useTranslation(["common", "onboarding", "company"]);
  const [key, setKey] = useState("");

  const [searchNOK, setSearchNOK] = useState(false);

  const initialErrors = {
    key: [],
    non_field_errors: [],
  };
  const [errors, setErrors] = useState(initialErrors);

  const clearErrors = () => setErrors(initialErrors);

  const handleSearch = async () => {
    clearErrors();
    setSearchNOK(false);
    const response = await api.searchCompaniesPublic(key);
    if (!response.ok) {
      setErrors({
        non_field_errors: [t("company:details.search.form.message.title")],
      });
      setSearchNOK(true);
      return;
    }

    const jsonResults = await response.json();
    if (jsonResults.length) {
      onSearchResults(jsonResults);
      return;
    }
    setErrors({
      key: [t("onboarding:forms.CompanyData.errors.noCompany")],
    });
    setSearchNOK(true);
  };

  const input = {
    name: "key",
    type: "text",
    floatingLabel: true,
    label: t("onboarding:inputs.company.label"),
    value: key,
    required: true,
    onChange: setKey,
    errors: errors.key,
  };

  return (
    <Form
      className="OnboardingForm"
      resetLoader={searchNOK}
      onSubmit={handleSearch}
      button={t("common:search")}
      errors={errors.non_field_errors}
    >
      <Input {...input} />
    </Form>
  );
}

function CompanyDisplayCards({ children, many = false }) {
  return (
    <div
      className="CompanyDisplayCards"
      style={many ? { paddingBottom: "1rem" } : {}}
    >
      {children}
    </div>
  );
}

function CompanyConfirmSearch({
  searchResults,
  handleConfirmCode,
  handleReset,
}) {
  const [selectedCode, setSelecteCode] = useState(searchResults[0].code);

  return (
    <div
      style={{
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
      }}
    >
      <Carousel
        keys={searchResults.map((searchResult) => searchResult.code)}
        onSelect={(currentCarouselCode) => setSelecteCode(currentCarouselCode)}
      >
        {searchResults.map((searchResult) => (
          <CompanyDisplayCard
            key={searchResult.code}
            localCompany={searchResult}
          />
        ))}
      </Carousel>

      <ModificationButton onClick={handleReset} />

      <ConfirmationButton
        onClick={() =>
          handleConfirmCode(
            searchResults.find(
              (searchResult) => searchResult.code === selectedCode,
            ),
          )
        }
      />
    </div>
  );
}

export function ContactDetails() {
  const { t } = useTranslation(["common", "account", "onboarding"]);
  const { handleDataChange, handleConfirm, data, errors } = useOnboarding();

  const keys = ["first_name", "surname", "telephone", "email"];

  const initialContact = Object.fromEntries(
    keys.map((key) => [key, data[key]]),
  );

  const [contact, setContact] = useState(initialContact);

  const handleContactChange = (key, value) => {
    setContact((prev) => ({
      ...prev,
      [key]: value,
    }));

    handleDataChange(key, value);
  };
  const [legalConditions, setLegalConditions] = useState(false);

  const inputs = [
    {
      label: t("common:first_name"),
      floatingLabel: true,
      type: "text",
      name: "first_name",
      autoComplete: "given-name",
      value: contact.first_name,
      maxLength: 50,
      onChange: (value) => handleContactChange("first_name", value),
      errors: errors.first_name,
    },
    {
      label: t("common:surname"),
      floatingLabel: true,
      type: "text",
      name: "surname",
      autoComplete: "family-name",
      value: contact.surname,
      maxLength: 50,
      onChange: (value) => handleContactChange("surname", value),
      errors: errors.surname,
    },
    {
      label: t("common:email"),
      floatingLabel: true,
      type: "email",
      name: "email",
      autoComplete: "email",
      value: contact.email,
      maxLength: 320,
      onChange: (value) => handleContactChange("email", value),
      errors: errors.email,
    },
    {
      label: t("common:telephone"),
      floatingLabel: true,
      name: "telephone",
      type: "tel",
      value: contact.telephone,
      onChange: (value) => handleContactChange("telephone", value),
      errors: errors.telephone,
    },
    {
      label: <LegalConditionsLabel />,
      type: "checkbox",
      name: "legal_terms",
      value: legalConditions,
      onChange: setLegalConditions,
    },
  ];

  return (
    <Form
      className="OnboardingForm"
      onSubmit={handleConfirm}
      button={t("onboarding:buttons:finish")}
      errors={errors.non_field_errors}
    >
      {inputs.map((input, index) => (
        <Input key={index} {...input} />
      ))}
    </Form>
  );
}

export function PartnersShowcase() {
  const { t } = useTranslation(["common", "onboarding"]);

  const tKey = "PartnersShowcase";

  const all = usePartners();
  const partnerToSkip = [
    "Aria",
    "CapEc",
    "GCollect",
    "Ocean",
    "October",
    "Silvr",
    "Unlimitd",
  ];
  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          marginBottom: "1.5rem",
          textAlign: "center",
        }}
      >
        <Typo variant="h2">{t("onboarding:asides." + tKey + ".line1")}</Typo>
        <Typo> {t("onboarding:asides." + tKey + ".line2")}</Typo>
      </div>

      <PartnersGrid
        className={"Onboarding"}
        IDs={all
          .filter((partner) => !partner.duplicate)
          .map((partner) => partner.id)
          .filter((partner) => !partnerToSkip.includes(partner))}
      />
    </div>
  );
}

export function FinancialDataAid() {
  const { t } = useTranslation(["common", "onboarding"]);

  const tKey = "onboarding:asides." + "FinancialDataAid";

  const sections = ["turnover", "profitability"];

  const sectionIcons = {
    turnover: <MonitorHeart />,
    profitability: <PieChart />,
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "2rem",
        maxWidth: "40ch",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: "0.5rem",
        }}
      >
        <Typo variant="h2">{t(tKey + ".title")}</Typo>
        <Typo variant="body4">{t(tKey + ".subtitle")}</Typo>
      </div>

      {sections.map((key) => (
        <FinancialDataAidSection key={key} icon={sectionIcons[key]}>
          <Trans
            t={t}
            i18nKey={tKey + "." + key}
            components={{
              em: (
                <em
                  style={{
                    fontStyle: "unset",
                    fontWeight: "700",
                  }}
                />
              ),
            }}
          />
        </FinancialDataAidSection>
      ))}
    </div>
  );
}

function FinancialDataAidSection({ children, icon }) {
  return (
    <div style={{ display: "flex", gap: "1rem", alignItems: "flex-start" }}>
      <div
        style={{
          borderRadius: "var(--borderRadiusMedium)",
          padding: "1rem",
          boxShadow: "var(--boxShadow2)",
          display: "flex",
        }}
      >
        {icon}
      </div>
      <Typo
        variant="body1"
        style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}
      >
        {children}
      </Typo>
    </div>
  );
}

export function DataSummary() {
  const { data } = useOnboarding();

  const { t } = useTranslation(["common", "onboarding"]);

  const keysGroup = ["equity", "gross_operating_profit"];

  const keys = ["net_income", "turnover", "amount", "need"];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        backgroundColor: "var(--color-card-background)",
        borderRadius: "var(--borderRadiusMedium)",
        padding: "1rem",
        maxWidth: "40ch",
      }}
    >
      <Typo
        variant="body3"
        style={{ fontWeight: "700", textAlign: "center", padding: "0.5rem" }}
      >
        {t("onboarding:asides.DataSummary.title")}
      </Typo>
      <DataGroup>
        {keysGroup.map((key) => (
          <DataLine key={key}>
            <DataLineLabel label={getDataLabel(t, key)} />
            <DataLineValue value={getDataTextValue(t, key, data[key]?.value)} />
          </DataLine>
        ))}
      </DataGroup>

      {keys.map((key) => (
        <DataGroup key={key}>
          <DataLine>
            <DataLineLabel label={getDataLabel(t, key)} />
            <DataLineValue value={getDataTextValue(t, key, data[key]?.value)} />
          </DataLine>
        </DataGroup>
      ))}
    </div>
  );
}

function DataGroup({ children }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        borderRadius: "var(--borderRadiusMedium)",
        backgroundColor: "white",
      }}
    >
      {children}
    </div>
  );
}

function DataLine({ children }) {
  return (
    <Typo
      variant="body3"
      className="DataLine"
      style={{
        display: "flex",
        gap: "3rem",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "1rem",
        borderBottom: "var(--border)",
      }}
    >
      {children}
    </Typo>
  );
}

function DataLineLabel({ label }) {
  return <span>{label}</span>;
}

function DataLineValue({ value }) {
  return <span style={{ textAlign: "right" }}>{value}</span>;
}

export function ClientReviews() {
  const { t } = useTranslation(["common", "onboarding"]);
  const { clientReviews } = useOnboarding();

  return (
    <div>
      <Cards className={"ClientReviewCards"}>
        {clientReviews.map((review) => (
          <Card className={"ClientReviewCard"} key={review.id}>
            <ImageContainer
              className="ClientReviewImageContainer"
              src={review.imgSrc}
            />
            <CardText>
              <CardTextHeading>{t(review.transKey + ".name")}</CardTextHeading>
              <CardTextSubheading title={t(review.transKey + ".company")}>
                {t(review.transKey + ".position")} -{" "}
                {t(review.transKey + ".company")}
              </CardTextSubheading>
              <CardTextSubheading title={t(review.transKey + ".review")}>
                <q>{t(review.transKey + ".review")}</q>
              </CardTextSubheading>
            </CardText>
          </Card>
        ))}
      </Cards>
    </div>
  );
}

function Carousel({ children, keys = null, onSelect }) {
  const childrenArray = React.Children.toArray(children);
  const totalChildren = childrenArray.length;

  const [currentIndex, setCurrentIndex] = useState(0);

  const selectedChild = childrenArray[currentIndex];

  const handleSelection = (direction = "next") => {
    let newIndex;

    switch (direction) {
      case "next":
        newIndex = (currentIndex + 1) % totalChildren;
        break;

      case "previous":
        newIndex = (currentIndex - 1 + totalChildren) % totalChildren;
        break;

      default:
        return;
    }

    setCurrentIndex(newIndex);
    keys && onSelect(keys[newIndex]);
  };

  const dispButtons = totalChildren > 1;

  if (totalChildren === 0) return <></>;
  return (
    <div
      className="Carousel"
      style={{ display: "flex", alignItems: "center", gap: "10px" }}
    >
      {dispButtons && (
        <Button
          style={{ visibility: currentIndex === 0 ? "hidden" : "visible" }}
          onClick={() => handleSelection("previous")}
        >
          <ArrowCircleLeft />
        </Button>
      )}

      <div style={{ flexGrow: 1, textAlign: "center" }}>{selectedChild}</div>

      {dispButtons && (
        <Button
          style={{
            visibility:
              currentIndex === totalChildren - 1 ? "hidden" : "visible",
          }}
          onClick={() => handleSelection("next")}
        >
          <ArrowCircleRight />
        </Button>
      )}
    </div>
  );
}

export function OnboardingPasswordSet() {
  const { t } = useTranslation(["common", "onboarding"]);

  const tKey = "onboarding:password";

  return (
    <Main themeSwitch>
      <Header style={{ alignItems: "center", textAlign: "center" }}>
        <Typo variant={"h1"}>{t(tKey + ".heading")}</Typo>
        <Typo variant={"body2"}>{t(tKey + ".subheading")}</Typo>
      </Header>
      <Content
        style={{ display: "flex", flexDirection: "column", gap: "2rem" }}
      >
        <PasswordSteps />
        <PasswordDiag />
        <PasswordProtect />
        <Supporters />
      </Content>
    </Main>
  );
}

function PasswordSteps() {
  const { t } = useTranslation(["common", "onboarding"]);

  const keys = ["check", "set", "access"];

  const iconsMap = {
    check: <Mail />,
    set: <Password />,
    access: <AutoAwesome />,
  };

  const tKey = "onboarding:password";

  const ps = t(tKey + ".steps", { returnObjects: true });

  return (
    <OnboardingCards>
      {keys.map((key) => (
        <OnboardingCard key={key}>
          <IconContainer style={{ marginBottom: "0.3rem" }}>
            {iconsMap[key]}
          </IconContainer>
          <Typo variant={"h2"}>{ps[key][0]}</Typo>
          <Typo>{ps[key][1]}</Typo>
        </OnboardingCard>
      ))}
    </OnboardingCards>
  );
}

function OnboardingCards({ children, style }) {
  return (
    <div className="OnboardingCards" style={style}>
      {children}
    </div>
  );
}

function OnboardingCard({ children, style }) {
  return (
    <div className="OnboardingCard" style={style}>
      {children}
    </div>
  );
}

function PasswordDiag() {
  const { t } = useTranslation(["common", "onboarding"]);

  const tKey = "onboarding:password";

  const ps = t(tKey + ".diag", { returnObjects: true });

  return (
    <div className="PasswordDiagCard">
      <div>
        <Send />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        <Typo variant={"body5"}>{ps[0]}</Typo>
        <Typo variant={"body3"} style={{ maxWidth: "unset" }}>
          {ps[1]}
        </Typo>
      </div>
    </div>
  );
}

function PasswordProtect() {
  const { t } = useTranslation(["common", "onboarding"]);

  const tKey = "onboarding:password";

  return (
    <div style={{ alignSelf: "center" }}>
      <Typo
        variant={"body3"}
        style={{ textAlign: "center", maxWidth: "unset" }}
      >
        {t(tKey + ".protect")}
      </Typo>
    </div>
  );
}

function IconContainer({ children, style }) {
  return (
    <div
      style={{
        display: "flex",
        padding: "1rem",
        alignItems: "center",
        backgroundColor: "var(--color-leano-3_95)",
        borderRadius: "var(--borderRadiusMedium)",
        ...style,
      }}
    >
      {children}
    </div>
  );
}
